import React, { useMemo, useState, useEffect, useRef } from "react";
import { css } from "@emotion/react";
import { useIdeasNodeStore } from "../../stores/ideaNodesStore";
import { timeStringToSeconds } from "./TimeReference";

const containerStyles = css`
  width: 100%;
  //   max-width: 48rem;
  margin: 0 auto;
  //   padding: 2rem;
  padding-top: 1.5rem;
  padding-bottom: 5px;
  user-select: none;
`;

const progressBarContainerStyles = css`
  position: relative;

  &:hover .time-label {
    opacity: 1;
  }
`;

const progressBarStyles = css`
  display: flex;
  width: 100%;
  height: 8px;
  border-radius: 8px;
  gap: 4px;
`;

const sectionStyles = css`
  position: relative;
  background-color: #717171;
  height: 100%;
  cursor: pointer;
`;

const middleSectionStyles = css`
  ${sectionStyles}
  background-color: #7b1a2e;
`;

const timeTooltipBaseStyles = css`
  position: absolute;
  z-index: 10;
  opacity: 0;
  transition: all 0.2s ease;
`;

const tooltipContentStyles = css`
  background-color: #1f2937;
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 14px;
  white-space: nowrap;
`;

export const TimelineSectionBar = (props: {
  timeStart: string;
  timeEnd: string;
}) => {
    const sessionMetadata = useIdeasNodeStore(
        (state) => state.sessionMetadata
      );

  const [shouldStackLabels, setShouldStackLabels] = useState(false);
  const startLabelRef = useRef(null);
  const endLabelRef = useRef(null);
  const middleSectionRef = useRef(null);

  const timeStart = props.timeStart;
  const timeEnd = props.timeEnd;
  const totalDuration = sessionMetadata?.contentDuration;

  const calculatePositions = useMemo(() => {
    const startSeconds = timeStringToSeconds(timeStart);
    const endSeconds = timeStringToSeconds(timeEnd);
    const total = parseInt(totalDuration ? totalDuration : '0');

    return {
      beforeStart: (startSeconds / total) * 100,
      duration: ((endSeconds - startSeconds) / total) * 100,
      afterEnd: ((total - endSeconds) / total) * 100,
    };
  }, [timeStart, timeEnd, totalDuration]);

  useEffect(() => {
    const checkOverlap = () => {
      if (
        !startLabelRef.current ||
        !endLabelRef.current ||
        !middleSectionRef.current
      )
        return;

      const startLabel = startLabelRef.current;
      const endLabel = endLabelRef.current;
      const middleSection = middleSectionRef.current;

      const middleWidth = middleSection.offsetWidth;
      const labelsWidth = startLabel.offsetWidth + endLabel.offsetWidth;

      // Add some padding to prevent labels from being too close
      const minRequiredWidth = labelsWidth + 20;

      setShouldStackLabels(middleWidth < minRequiredWidth);
    };

    checkOverlap();
    window.addEventListener("resize", checkOverlap);
    return () => window.removeEventListener("resize", checkOverlap);
  }, [calculatePositions]);

  const startLabelStyles = css`
    ${timeTooltipBaseStyles}
    left: ${calculatePositions.beforeStart}%;
    transform: translateX(-50%);
    ${shouldStackLabels ? "bottom: calc(100% + 8px);" : "top: -28px;"}
  `;

  const endLabelStyles = css`
    ${timeTooltipBaseStyles}
    left: ${calculatePositions.beforeStart + calculatePositions.duration}%;
    transform: translateX(-50%);
    ${shouldStackLabels ? "top: calc(100% + 8px);" : "top: -28px;"}
  `;

  return (
    <>
      {!timeStart || !timeEnd || !totalDuration ? (
        <></>
      ) : (
        <>
          <div css={containerStyles}>
            <div css={progressBarContainerStyles}>
              <div css={progressBarStyles}>
                {/* Before start section */}
                <div
                  css={sectionStyles}
                  style={{ width: `${calculatePositions.beforeStart}%` }}
                />

                {/* Between start and end section */}
                <div
                  ref={middleSectionRef}
                  css={middleSectionStyles}
                  style={{ width: `${calculatePositions.duration}%` }}
                />

                {/* After end section */}
                <div
                  css={sectionStyles}
                  style={{ width: `${calculatePositions.afterEnd}%` }}
                />
              </div>

              {/* Time labels positioned relative to the container */}
              <div
                ref={startLabelRef}
                css={startLabelStyles}
                className="time-label"
              >
                <div css={tooltipContentStyles}>{timeStart}</div>
              </div>

              <div
                ref={endLabelRef}
                css={endLabelStyles}
                className="time-label"
              >
                <div css={tooltipContentStyles}>{timeEnd}</div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};
